import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { loadUser } from "../../../store/userSlice";
import { Link, useNavigate } from "react-router-dom";
import {
  getAllDocsSnap,
  LogOutUser,
  queryAllDocsByFieldSnap,
} from "../../../utils/firebase/Firebase.utils";
import BlankPhoto from "../../assets/blankphoto.webp";

import toast from "react-hot-toast";

import { IoMdMailUnread } from "react-icons/io";
import { IoIosSettings } from "react-icons/io";
import { FaUser } from "react-icons/fa";
import { RiLogoutBoxRFill } from "react-icons/ri";
import { MdWarning } from "react-icons/md";

function UserBar() {
  const currentUser = useSelector((state) => state.userObject);
  const dispatch = useDispatch();
  const [notifications, setNotifications] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [lastItem, setLastItem] = React.useState(null);
  const [users, setUsers] = React.useState([]);
  const [emergencies, setEmergencies] = React.useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      await getAllDocsSnap("users", (data) => {
        setUsers(data);
        setLoading(false);
      });
    };
    fetchUsers();
  }, []);

  // Navigate to Stacking Plan page
  const navigateToStackingPlan = () => {
    navigate("/resident-management?link=Stacking+Plan");
  };

  useEffect(() => {
    // Subscribe to emergencies collection for active emergencies
    const unsubscribe = queryAllDocsByFieldSnap(
      "emergencies",
      "status",
      "==",
      "Active",
      (emergencyData) => {
        setEmergencies(emergencyData);

        // Show toast notification for new emergencies
        if (emergencyData.length > 0 && !loading) {
          // Sort by timestamp to get the most recent emergency
          const sortedEmergencies = [...emergencyData].sort((a, b) => {
            const dateA = new Date(a.timestamp || a.createdAt || 0);
            const dateB = new Date(b.timestamp || b.createdAt || 0);
            return dateB - dateA;
          });

          const latestEmergency = sortedEmergencies[0];

          // Only show toast if this is a new emergency (within last 60 seconds)
          const emergencyTime = new Date(
            latestEmergency.timestamp || latestEmergency.createdAt
          );
          const currentTime = new Date();
          const timeDiff = (currentTime - emergencyTime) / 1000; // in seconds

          if (timeDiff < 60) {
            toast.custom((t) => (
              <div
                className={`${
                  t.visible ? "animate-enter" : "animate-leave"
                } max-w-md w-full bg-red-50 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-red-500 ring-opacity-5`}
              >
                <div className="flex-1 w-0 p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 pt-0.5">
                      <MdWarning className="h-10 w-10 text-red-600" />
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-semibold text-red-900">
                        Emergency Alert: Unit {latestEmergency.unitNumber}
                      </p>
                      <p className="mt-1 text-sm text-red-700">
                        {latestEmergency.type}: {latestEmergency.details}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex border-l border-red-200">
                  <button
                    onClick={() => {
                      navigateToStackingPlan();
                      toast.dismiss(t.id);
                    }}
                    className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ));
          }
        }
      }
    );

    return () => unsubscribe && unsubscribe();
  }, [loading, navigate]);

  const newUsers = users.filter((user) => user.roles === undefined);
  const newUserCount = newUsers.length;

  useEffect(() => {
    const fetchNotifications = async () => {
      await getAllDocsSnap("notifications", (data) => {
        const filteredNotifications = data.filter(
          (item) => item.read === false && item.to === currentUser.email
        );
        setNotifications(filteredNotifications.length);
        dispatch(
          loadUser({
            ...currentUser,
            notifications: filteredNotifications.length,
          })
        );
        setLastItem(filteredNotifications[filteredNotifications.length - 1]);
      });
    };
    fetchNotifications();
  }, []);

  const userPhoto = users.find(
    (user) => user.email === lastItem?.from
  )?.photoUrl;

  useEffect(() => {
    if (notifications > 0 && loading === false) {
      toast.custom(
        (t) => (
          <div
            className={`${
              t.visible ? "animate-enter" : "animate-leave"
            } max-w-md w-full bg-eleghoBlue text-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
          >
            <div className="flex-1 w-0 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <img
                    className="h-10 w-10 rounded-full"
                    src={userPhoto || BlankPhoto}
                    alt=""
                  />
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-white">
                    New Notification Received
                  </p>
                  <p className="mt-1 text-sm text-gray-200">
                    {lastItem?.subject}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex border-l border-gray-200">
              <button
                onClick={() => toast.dismiss(t.id)}
                className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-white hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Close
              </button>
            </div>
          </div>
        ),
        {
          duration: 800,
          position: "bottom-right",
        }
      );
    }
  }, [notifications]);

  const handleLogout = async () => {
    await LogOutUser();
    navigate("/login");
    dispatch(loadUser({}));
  };

  const getEmergencyIcon = (type) => {
    const emergencyTypes = {
      "Fire Alarm": "🔥",
      "Water Leak": "💧",
      "Medical Emergency": "🚑",
      "Security Breach": "🚨",
      "Elevator Malfunction": "⚠️",
    };
    return emergencyTypes[type] || "⚠️";
  };

  // Get the highest priority emergency
  const getHighestPriorityEmergency = () => {
    if (emergencies.length === 0) return null;

    const priorityValues = {
      High: 3,
      Medium: 2,
      Low: 1,
    };

    return emergencies.reduce((highest, current) => {
      const currentPriority = priorityValues[current.priority] || 0;
      const highestPriority = priorityValues[highest.priority] || 0;

      return currentPriority > highestPriority ? current : highest;
    }, emergencies[0]);
  };

  const highestPriorityEmergency = getHighestPriorityEmergency();

  return (
    <div className="flex items-center justify-center gap-2 text-gray-500 ">
      {emergencies.length > 0 && (
        <button
          onClick={navigateToStackingPlan}
          className="flex items-center bg-red-100 text-red-800 hover:bg-red-200 px-3 py-1 rounded-full mr-2 animate-pulse"
        >
          {highestPriorityEmergency && (
            <span className="mr-1">
              {getEmergencyIcon(highestPriorityEmergency.type)}
            </span>
          )}
          <MdWarning className="mr-1" />
          <span className="text-xs font-semibold">
            {emergencies.length > 1
              ? `${emergencies.length} Active Emergencies`
              : `Emergency: Unit ${emergencies[0]?.unitNumber}`}
          </span>
        </button>
      )}

      <div className="hover:bg-gray-400 hover:text-white cursor-pointer p-2 rounded-lg text-xl">
        <Link to="/notifications" className="relative">
          <IoMdMailUnread />
          {notifications > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full px-2 absolute -top-2 -right-2">
              {notifications}
            </span>
          )}
        </Link>
      </div>

      <div className="hover:bg-gray-400 hover:text-white cursor-pointer p-2 rounded-lg text-xl relative">
        <Link to="/settings?link=setup">
          <IoIosSettings />
          <div className="bg-red-500 text-white text-xs rounded-full px-2 absolute -top-0 -right-0">
            {newUserCount > 0 && newUserCount}
          </div>
        </Link>
      </div>
      <div className="hover:bg-gray-400 hover:text-white cursor-pointer p-2 rounded-lg text-xl">
        <Link to="/settings?link=profile">
          <FaUser />
        </Link>
      </div>
      <button
        className="hover:bg-gray-400 hover:text-white cursor-pointer p-2 rounded-lg text-xl"
        onClick={handleLogout}
      >
        <RiLogoutBoxRFill />
      </button>
    </div>
  );
}

export default UserBar;
